import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { type CreativeModelFactory } from '@bynder-studio/render-web';
import { isFullyAccurate } from '@bynder-studio/render-core';
import useCreativeModelFactory, { DesignConfig } from 'packages/hooks/useCreativeModelFactory';
import { getFonts } from 'packages/store/user/user.selectors';
import componentService, { Component, PartialComponent } from 'packages/services/componentService';
import { buildDesignConfigFromComponent } from './utils-thumbnail';
import { ignoreAbortErrors, makeAbortable } from 'packages/helpers/helpers';

const PREVIEW_SIZE: [number, number] = [256, 256];
const DEBOUNCE_DELAY = 500;

export type UseComponentThumbnailResult = {
    thumbnailUrl: string | null;
    isLoading: boolean;
    error: Error | null;
};

export function useComponentThumbnail(component: PartialComponent): UseComponentThumbnailResult {
    const fonts = useSelector(getFonts);

    const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(component.thumbnailUrl || null);
    const [isLoading, setIsLoading] = useState<boolean>(!component.thumbnailUrl);
    const [error, setError] = useState<Error | null>(null);
    const [fullComponent, setFullComponent] = useState<Component | null>(null);

    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const renderCleanupRef = useRef<{ abort: () => void } | null>(null);

    const designConfig = useMemo<DesignConfig | null>(() => {
        if (!fullComponent?.elements?.length) {
            return null;
        }

        return buildDesignConfigFromComponent(fullComponent);
    }, [fullComponent]);

    const factoryInstance = useCreativeModelFactory(component.type, fonts, designConfig);

    const startRender = useCallback(
        (factory: CreativeModelFactory) => {
            renderCleanupRef.current?.abort();

            const renderer = factory.getPagePreviewRenderer();
            renderer.setPreviewSize(PREVIEW_SIZE[0], PREVIEW_SIZE[1]);

            const abortController = new AbortController();

            const removeEndListener = renderer.onRenderEnd(({ dataUrl, accuracy }) => {
                if (!dataUrl) {
                    return;
                }

                setThumbnailUrl(dataUrl);
                setIsLoading(false);
                component.thumbnailUrl = dataUrl;

                if (!isFullyAccurate(accuracy)) {
                    return;
                }

                if (debounceTimerRef.current) {
                    clearTimeout(debounceTimerRef.current);
                }

                debounceTimerRef.current = setTimeout(() => {
                    return componentService.storeThumbnail(component.id, component.thumbnailUrl);
                }, DEBOUNCE_DELAY);
            });

            renderCleanupRef.current = {
                abort: () => {
                    abortController.abort();
                    removeEndListener();
                },
            };

            makeAbortable(renderer.render(0), abortController).catch(ignoreAbortErrors);
        },
        [component],
    );

    useEffect(() => {
        if (thumbnailUrl) {
            return;
        }

        const abortController = new AbortController();

        makeAbortable(componentService.getComponentById(component.id.toString()), abortController)
            .then((response) => {
                if (response.status === 200) {
                    setFullComponent(response.json);
                }
            })
            .catch(ignoreAbortErrors);

        return () => {
            abortController.abort();
        };
    }, [component.id, thumbnailUrl]);

    useEffect(() => {
        if (!factoryInstance) {
            return;
        }

        startRender(factoryInstance);

        return () => {
            renderCleanupRef.current?.abort();

            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [factoryInstance, startRender]);

    return { thumbnailUrl, isLoading, error };
}
